import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { DateTimeRangePickerValue } from '@/components/RunsTable/utils/getDateTimeRangeLabel';
import { tryJsonParseSearchParam } from '@/hooks/useDataGridState';
import { useDefaultDuration } from '@/hooks/useDefaultDuration';
import { RunStatsGroupBy, TimedeltaInput } from '@/types/schema';

import { useChartTimeFilter } from './useChartTimeFilter';

export interface SearchParamChartDisplayConfig {
  timeModel: DateTimeRangePickerValue;
  setTimeModel: (newTimeModel: DateTimeRangePickerValue) => void;
  groupBySelection: RunStatsGroupBy | undefined;
  setGroupBySelection: (newGroupBy: RunStatsGroupBy | undefined) => void;
  customChartsParams: {
    start_time: string;
    end_time: string;
    stride: TimedeltaInput;
  };
}

export const useSearchParamChartDisplayConfig = (
  defaultDurationKey: Parameters<typeof useDefaultDuration>[0],
  options?: {
    includeGroupBy?: boolean;
  }
): SearchParamChartDisplayConfig => {
  const { includeGroupBy } = options ?? {};
  const [searchParams, setSearchParams] = useSearchParams();
  const { defaultDurationTimeModel } = useDefaultDuration(defaultDurationKey);

  // Read directly from URL as single source of truth
  const timeModel = useMemo(
    () =>
      tryJsonParseSearchParam(
        searchParams,
        'timeModel',
        defaultDurationTimeModel
      ),
    [searchParams, defaultDurationTimeModel]
  );

  const groupBySelection = useMemo(
    () =>
      includeGroupBy
        ? tryJsonParseSearchParam(searchParams, 'groupBy', undefined)
        : undefined,
    [searchParams, includeGroupBy]
  );

  const timeFilter = useChartTimeFilter({ timeRange: timeModel });
  const customChartsParams = timeFilter.customChartsParams;

  const setTimeModel = (newTimeModel: DateTimeRangePickerValue) => {
    timeFilter.setTimeModel(newTimeModel);
    setSearchParams(
      (prev) => {
        if (newTimeModel && Object.keys(newTimeModel).length > 0) {
          prev.set('timeModel', JSON.stringify(newTimeModel));
        } else {
          prev.delete('timeModel');
        }
        return prev;
      },
      { replace: true }
    );
  };

  const setGroupBySelection = (newGroupBy: RunStatsGroupBy | undefined) => {
    if (!includeGroupBy) return;

    setSearchParams(
      (prev) => {
        if (newGroupBy) {
          prev.set('groupBy', JSON.stringify(newGroupBy));
        } else {
          prev.delete('groupBy');
        }
        return prev;
      },
      { replace: true }
    );
  };

  return {
    timeModel,
    setTimeModel,
    groupBySelection,
    setGroupBySelection,
    customChartsParams,
  };
};
